import { apiFetch } from '@/lib/api';

export interface AssessmentDTO {
  id: string;
  organization_id: string;
  event_id: string;
  name: string;
  created_by: string;
  status: 'draft' | 'configured' | 'estimating' | 'completed' | 'failed';
  destinations_config: DestinationConfig[];
  metadata?: any;
  created_at: string;
  updated_at: string;
  // Extended fields from joins
  event_name: string;
  event_client_name: string;
  attendee_count: number;
  origin_airport_count: number;
}

export interface DestinationConfig {
  city: string;
  place_id: string;
  formatted_address: string;
  lat: number;
  lon: number;
  selected_airports: string[]; // IATA codes
  available_airports?: AirportOption[]; // All nearby airports found
}

export interface AirportOption {
  iata: string;
  name: string;
  distance_km: number;
  type: string;
}

export interface CreateAssessmentRequest {
  event_id: string;
  name: string;
  destinations_config: DestinationConfig[];
  metadata?: any;
}

export interface UpdateAssessmentRequest {
  name?: string;
  destinations_config?: DestinationConfig[];
  metadata?: any;
  status?: AssessmentDTO['status'];
}

export interface EventOption {
  id: string;
  name: string;
  client_name: string;
  attendee_count: number;
  origin_airport_count: number;
}

export class AssessmentsService {
  async list(page = 1, pageSize = 20): Promise<{
    items: AssessmentDTO[];
    pagination: {
      page: number;
      pageSize: number;
      totalItems: number;
      totalPages: number;
    };
  }> {
    const res = await apiFetch(`/api/assessments?page=${page}&pageSize=${pageSize}`);
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to load assessments' } }));
      throw new Error(errorData.error?.message || 'Failed to load assessments');
    }
    const data = await res.json();
    return data.data;
  }

  async getById(id: string): Promise<AssessmentDTO> {
    const res = await apiFetch(`/api/assessments/${id}`);
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to load assessment' } }));
      throw new Error(errorData.error?.message || 'Failed to load assessment');
    }
    const data = await res.json();
    return data.data;
  }

  async create(input: CreateAssessmentRequest): Promise<{ id: string; assessment: AssessmentDTO }> {
    const res = await apiFetch('/api/assessments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to create assessment' } }));
      throw new Error(errorData.error?.message || 'Failed to create assessment');
    }
    const data = await res.json();
    return data.data;
  }

  async update(id: string, updates: UpdateAssessmentRequest): Promise<AssessmentDTO> {
    const res = await apiFetch(`/api/assessments/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to update assessment' } }));
      throw new Error(errorData.error?.message || 'Failed to update assessment');
    }
    const data = await res.json();
    return data.data;
  }

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`/api/assessments/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to delete assessment' } }));
      throw new Error(errorData.error?.message || 'Failed to delete assessment');
    }
  }

  async getEventOptions(opts: { includeEmpty?: boolean } = {}): Promise<EventOption[]> {
    const res = await apiFetch(`/api/assessments/events${opts.includeEmpty ? '?include_empty=true' : ''}`);
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to load events' } }));
      throw new Error(errorData.error?.message || 'Failed to load events');
    }
    const data = await res.json();
    return data.data;
  }

  async resolveDestinationAirports(lat: number, lon: number, limit = 5): Promise<AirportOption[]> {
    const res = await apiFetch(`/api/assessments/resolve-airports?lat=${lat}&lon=${lon}&limit=${limit}`);
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: { message: 'Failed to resolve airports' } }));
      throw new Error(errorData.error?.message || 'Failed to resolve airports');
    }
    const data = await res.json();
    return data.data;
  }

  getStatusBadgeVariant(status: AssessmentDTO['status']): 'default' | 'secondary' | 'success' | 'warning' | 'danger' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'estimating':
        return 'warning';
      case 'failed':
        return 'danger';
      case 'configured':
        return 'secondary';
      case 'draft':
      default:
        return 'default';
    }
  }

  getStatusLabel(status: AssessmentDTO['status']): string {
    switch (status) {
      case 'draft':
        return 'Draft';
      case 'configured':
        return 'Configured';
      case 'estimating':
        return 'Estimating';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  }
}